import { Permission } from '../enums/permission.enum';
import { PermissionBit } from '../utils/permission-types';

/**
 * Route-Permission mapping configuration
 * Maps route paths to required permissions for access
 */
export interface RoutePermissionConfig {
  [route: string]: PermissionBit[];
}

const INVOICING_ROUTE_PERMISSIONS: PermissionBit[] = [
  Permission.INVOICING_READ,
  Permission.INVOICING_SERVICE,
  Permission.INVOICING_ADMIN,
];

const DASHBOARD_ROUTE_PERMISSIONS: PermissionBit[] = [
  Permission.DASHBOARD_GLOBAL,
  Permission.DASHBOARD_CLIENT,
  Permission.DASHBOARD_LEADER,
  Permission.DASHBOARD_LANGUAGE,
  Permission.DASHBOARD_PROMOTION,
  Permission.DASHBOARD_MEDICAL_VISIT,
];

/**
 * The Dashboard hosts the "Campaign Status" tab, which is reserved to
 * Evaluation Write users (see HomeComponent.TAB_REQUIREMENTS and the
 * *emPermission guard in the template). Evaluation Write must therefore be
 * accepted by the route, otherwise those users can never reach the tab.
 * The other tabs keep their own Dashboard permission.
 */
const STAFFING_DASHBOARD_ROUTE_PERMISSIONS: PermissionBit[] = [
  ...DASHBOARD_ROUTE_PERMISSIONS,
  Permission.EVALUATION_WRITE,
];

export const ROUTE_PERMISSIONS: RoutePermissionConfig = {
  // Employee routes
  employees: DASHBOARD_ROUTE_PERMISSIONS,
  'employees/card-view': [Permission.CARDS_VIEW_READ],
  'employees/grid-view': [Permission.CARDS_VIEW_READ],
  'employees/interns': [Permission.INTERNSHIP_LISTS_READ, Permission.INTERNSHIP_LISTS_WRITE],
  'employees/interns/new': [Permission.INTERNSHIP_REQUEST_WRITE],
  'employees/details': [Permission.CARDS_VIEW_READ],
  'employees/details/:number': [Permission.CARD_VIEW_OWN_PAGE, Permission.CARDS_VIEW_READ],
  'employees/new': [Permission.EXPORT_IMPORT_IMPORT_EMPLOYEE],
  'employees/hc-global': [Permission.CARDS_VIEW_READ],
  'employees/role-validation': [Permission.CARD_VIEW_VALIDATE_ROLE],
  'employees/salary-validation': [Permission.SALARY_VALIDATE],
  
  'employees/contract-ending': [Permission.CONTRACT_ENDING_NOTIFICATION],
  'employees/trial-period': [Permission.TRIAL_PERIOD_ENDING_NOTIFICATION],
  'employees/junior-promotion': [Permission.JUNIOR_PROMOTION_NOTIFICATION],

  // Staffing routes
  staffing: [Permission.STAFFING_READ],
  'staffing/dashboard': STAFFING_DASHBOARD_ROUTE_PERMISSIONS,
  'staffing/client': [Permission.CLIENT_READ],
  'staffing/project': [Permission.PROJECT_READ],
  'staffing/staff': [Permission.STAFFING_READ],
  'staffing/promotions': [
  Permission.PROMOTION_READ,
  Permission.PROMOTION_IDENTIFY_CANDIDATE,
  Permission.PROMOTION_CONFIRM_IDENTIFICATION,
  Permission.PROMOTION_COMMIT,
  Permission.PROMOTION_SCHEDULE_INTERVIEW,
  Permission.PROMOTION_MANAGE_CANDIDATE,
  ],
  'staffing/objectives': [Permission.STAFFING_READ],
  'staffing/configuration': [Permission.STAFFING_WRITE],
  'staffing/configuration/role-mgmt': [Permission.STAFFING_WRITE],
  'staffing/configuration/role-users': [Permission.STAFFING_WRITE],
  'staffing/simulation': [Permission.STAFFING_READ],
  'staffing/evaluation': [Permission.EVALUATION_READ],
  'staffing/evaluation-form': [Permission.EVALUATION_READ, Permission.EVALUATION_WRITE],
  'staffing/midYear-review-form': [Permission.EVALUATION_READ, Permission.EVALUATION_WRITE],
  'staffing/evaluation-employee': [Permission.EVALUATION_READ],
  'staffing/evaluation-employee/evaluation-employee-list': [Permission.EVALUATION_READ],
  'staffing/evaluation-employee/employee-evaluations': [Permission.EVALUATION_READ],
  'staffing/evaluation-employee/self-evaluation-form': [Permission.EVALUATION_READ, Permission.EVALUATION_WRITE],
  'staffing/evaluation-employee/self-evaluation-list': [Permission.EVALUATION_READ],
  'staffing/evaluation-employee/campaign-list': [Permission.EVALUATION_READ],
  'staffing/evaluation-employee/add-permissions': [Permission.EVALUATION_WRITE],
  'staffing/evaluation-employee/assign-reviewers': [Permission.EVLUATION_ASSIGN_REVIEWERS],
  'staffing/evaluation-employee/remuneration': [
    Permission.MANAGER_READ, Permission.MANAGER_WRITE, Permission.MANAGER_PEOPLE,
    Permission.PEOPLE_READ, Permission.PEOPLE_WRITE, Permission.DIRECTOR_READ,
    Permission.DIRECTOR_WRITE, Permission.EVALUATION_READ, Permission.EVALUATION_WRITE
  ],
  'staffing/evaluation-employee/review-impact': [
    Permission.MANAGER_READ, Permission.MANAGER_WRITE, Permission.MANAGER_PEOPLE,
    Permission.PEOPLE_READ, Permission.PEOPLE_WRITE, Permission.DIRECTOR_READ,
    Permission.DIRECTOR_WRITE, Permission.EVALUATION_WRITE,
  ],

  // Pool management routes
  'pool-management': [Permission.POOL_READ, Permission.POOL_WRITE],

  // Interview routes
  interview: [Permission.PROMOTION_READ],

  // Configurations routes
  configurations: [
    Permission.CONFIG_STAFFING,
    Permission.CONFIG_BSA,
    Permission.CONFIG_INVOICING,
    Permission.CONFIG_PERFORMANCE,
    Permission.CONFIG_USER_MANAGEMENT,
    Permission.MANAGER_READ,
    Permission.MANAGER_WRITE,
    Permission.DIRECTOR_READ,
    Permission.DIRECTOR_WRITE,
  ],
  'configurations/permissions': [Permission.CONFIG_USER_MANAGEMENT],
  'configurations/users': [Permission.CONFIG_USER_MANAGEMENT],
  'configurations/roles': [Permission.CONFIG_USER_MANAGEMENT],
  'configurations/technology': [Permission.CONFIG_STAFFING],
  'configurations/skill-category': [Permission.CONFIG_STAFFING],
  'configurations/sector': [Permission.CONFIG_STAFFING],
  'configurations/nearshore': [Permission.CONFIG_STAFFING],
  'configurations/cluster': [Permission.CONFIG_STAFFING],
  'configurations/offshore-level': [Permission.CONFIG_STAFFING],
  'configurations/career-path': [Permission.CONFIG_BSA],
  'configurations/category': [Permission.CONFIG_BSA],
  'configurations/country': [Permission.CONFIG_BSA],
  'configurations/region': [Permission.CONFIG_BSA],
  'configurations/status': [Permission.CONFIG_BSA],
  'configurations/runtime-settings': [Permission.CONFIG_BSA],
  'configurations/document-template': [Permission.CONFIG_BSA],
  'configurations/staffed-role': [Permission.CONFIG_INVOICING],
  'configurations/working-hours': [Permission.CONFIG_INVOICING],
  'configurations/invoicing-settings': [Permission.CONFIG_INVOICING],
  'configurations/guard-configuration': [Permission.CONFIG_INVOICING],
  'configurations/jira-project-keys': [Permission.CONFIG_INVOICING],
  'configurations/invoicing-project-accounts': [Permission.CONFIG_INVOICING],
  'configurations/currency': [Permission.CONFIG_INVOICING],
  'configurations/currency-exchange': [Permission.CONFIG_INVOICING],
  'configurations/evaluation': [
    Permission.CONFIG_PERFORMANCE,
    Permission.MANAGER_READ,
    Permission.MANAGER_WRITE,
    Permission.DIRECTOR_READ,
    Permission.DIRECTOR_WRITE,
  ],
  'configurations/opportunities': [Permission.CONFIG_PERFORMANCE],
  'configurations/pool': [Permission.CONFIG_PERFORMANCE],

  // Opportunities routes
  'opportunities/opportunities-list': [Permission.OPPORTUNITY_READ],
  'opportunities/demandes': [Permission.DEMAND_READ],
  'opportunities/bad-attitude': [Permission.PEOPLE_WITH_BAD_ATTITUDE_READ],
  'opportunities/release': [Permission.PEOPLE_AT_RISK_READ],

  // Audit Log
  'audit-log': [Permission.AUDIT_LOG_READ],

  // Feedback routes
  feedback: [],
  'feedback/all': [Permission.FEEDBACK_ASSIGNMENT_MANAGER],
  'feedback/all/:id': [Permission.FEEDBACK_ASSIGNMENT_MANAGER],
  'feedback/assigned': [Permission.FEEDBACK_ASSIGNED_HANDLER, Permission.FEEDBACK_ASSIGNMENT_MANAGER],
  'feedback/assigned/:id': [Permission.FEEDBACK_ASSIGNED_HANDLER, Permission.FEEDBACK_ASSIGNMENT_MANAGER],
  'feedback/my': [],
  'feedback/my/:id': [],

  // Invoicing routes
  'finance': INVOICING_ROUTE_PERMISSIONS,
  'finance/group': INVOICING_ROUTE_PERMISSIONS,
  'finance/process': INVOICING_ROUTE_PERMISSIONS,
  'finance/process/reports': [Permission.INVOICING_ADMIN],
  'finance/process/closing-reports': [Permission.INVOICING_ADMIN],
  'finance/process/extra-costs': [Permission.INVOICING_ADMIN],
  'finance/process/:serviceId': INVOICING_ROUTE_PERMISSIONS,
  'finance/group/:groupId/worktime': INVOICING_ROUTE_PERMISSIONS,
  'finance/group/:groupId/adjustments': INVOICING_ROUTE_PERMISSIONS,

  // Production routes (Service moved here from Finance)
  'production': [Permission.PRODUCTION],
  'production/service': INVOICING_ROUTE_PERMISSIONS,
  'production/service/:serviceId/teams': INVOICING_ROUTE_PERMISSIONS,
  'production/unsigned': [Permission.PRODUCTION, Permission.PEOPLE_READ, Permission.MANAGER_READ],
  'production/with-supervisor': [Permission.PRODUCTION, Permission.PEOPLE_READ, Permission.MANAGER_READ],

  // BSA Team routes
  'bsa-team': [Permission.BSA_ORG_READ],
  'bsa-team/organisation': [Permission.BSA_ORG_READ],
  'bsa-team/organisation/manage': [Permission.BSA_ORG_WRITE],
  'bsa-team/posts': [Permission.BSA_ORG_READ],
  'bsa-team/posts/structure': [Permission.BSA_ORG_READ],
  'bsa-team/tasks': [Permission.BSA_ORG_READ],

  // Demo routes (accessible to all authenticated users)
  'demo-permissions': [],
};


function matchesPattern(routeSegment: string, patternSegment: string): boolean {

  if (patternSegment.startsWith(':')) {
    return true;
  }

  return routeSegment === patternSegment;
}


function matchesRoutePattern(routePath: string, pattern: string): boolean {
  const routeParts = routePath.split('/').filter((p) => p);
  const patternParts = pattern.split('/').filter((p) => p);

  if (routeParts.length !== patternParts.length) {
    return false;
  }

  return routeParts.every((routePart, index) => matchesPattern(routePart, patternParts[index]));
}

/**
 * Helper function to get required permissions for a route
 * @param route The route path
 * @returns Array of required permissions or empty array if no permissions required
 */
export function getRoutePermissions(route: string): PermissionBit[] {
  // Try exact match first
  if (ROUTE_PERMISSIONS[route]) {
    return ROUTE_PERMISSIONS[route];
  }

  const cleanRoute = route.replace(/^\//, '').split('?')[0];

  for (const [pattern, permissions] of Object.entries(ROUTE_PERMISSIONS)) {
    if (matchesRoutePattern(cleanRoute, pattern)) {
      return permissions;
    }
  }

  // Try to find a parent route match
  const routeParts = route.split('/');
  for (let i = routeParts.length - 1; i > 0; i--) {
    const parentRoute = routeParts.slice(0, i).join('/');
    if (ROUTE_PERMISSIONS[parentRoute]) {
      return ROUTE_PERMISSIONS[parentRoute];
    }
  }

  // No permissions required if route not found
  return [];
}

/**
 * Helper function to check if a route requires any permissions
 * @param route The route path
 * @returns True if route requires permissions, false otherwise
 */
export function routeRequiresPermissions(route: string): boolean {
  return getRoutePermissions(route).length > 0;
}
